import React, { useEffect, useMemo, useState } from 'react';
import PageLayout from '../components/pagelayout';
import { supabase } from '../supabase';
import './exhibitorsManagement.css';

const emptyForm = {
  name: '',
  description: '',
  category: '',
  audience: '',
  language: '',
  price_range: '',
  hall: '',
  booth: '',
  website: '',
  email: '',
  logo_emoji: '📚',
  logo_url: '',
  status: 'Approved',
  is_international: false,
  is_featured: false,
};

export default function ExhibitorsManagement() {
  const [exhibitors, setExhibitors] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const loadExhibitors = async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from('exhibitors')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error(error);
      setMessage('Failed to load exhibitors.');
    } else {
      setExhibitors(data || []);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadExhibitors();

    const channel = supabase
      .channel('exhibitors_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'exhibitors',
        },
        () => loadExhibitors()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const stats = useMemo(() => {
    const total = exhibitors.length;
    const approved = exhibitors.filter((ex) => ex.status === 'Approved').length;
    const international = exhibitors.filter((ex) => ex.is_international).length;
    const pending = exhibitors.filter((ex) => ex.status === 'Pending').length;

    return { total, approved, international, pending };
  }, [exhibitors]);

  const filteredExhibitors = exhibitors.filter((exhibitor) => {
    const matchesSearch =
      exhibitor.name?.toLowerCase().includes(search.toLowerCase()) ||
      exhibitor.category?.toLowerCase().includes(search.toLowerCase()) ||
      exhibitor.hall?.toLowerCase().includes(search.toLowerCase()) ||
      exhibitor.booth?.toLowerCase().includes(search.toLowerCase());

    const matchesCategory = categoryFilter
      ? exhibitor.category === categoryFilter
      : true;

    const matchesStatus = statusFilter
      ? exhibitor.status === statusFilter
      : true;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const categories = [...new Set(exhibitors.map((ex) => ex.category).filter(Boolean))];

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const saveExhibitor = async () => {
    if (!form.name || !form.hall || !form.booth) {
      setMessage('Please add exhibitor name, hall, and booth.');
      return;
    }

    setMessage('');

    const payload = {
      ...form,
      updated_at: new Date().toISOString(),
    };

    let result;

    if (editingId) {
      result = await supabase
        .from('exhibitors')
        .update(payload)
        .eq('id', editingId);
    } else {
      result = await supabase
        .from('exhibitors')
        .insert(payload);
    }

    if (result.error) {
      console.error(result.error);
      setMessage('Save failed: ' + result.error.message);
    } else {
      setMessage(editingId ? 'Exhibitor updated successfully.' : 'Exhibitor added successfully.');
      resetForm();
      loadExhibitors();
    }
  };

  const editExhibitor = (exhibitor) => {
    setEditingId(exhibitor.id);
    setForm({
      name: exhibitor.name || '',
      description: exhibitor.description || '',
      category: exhibitor.category || '',
      audience: exhibitor.audience || '',
      language: exhibitor.language || '',
      price_range: exhibitor.price_range || '',
      hall: exhibitor.hall || '',
      booth: exhibitor.booth || '',
      website: exhibitor.website || '',
      email: exhibitor.email || '',
      logo_emoji: exhibitor.logo_emoji || '📚',
      logo_url: exhibitor.logo_url || '',
      status: exhibitor.status || 'Approved',
      is_international: !!exhibitor.is_international,
      is_featured: !!exhibitor.is_featured,
    });

    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const deleteExhibitor = async (id) => {
    const { error } = await supabase
      .from('exhibitors')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(error);
      setMessage('Delete failed.');
    } else {
      setMessage('Exhibitor deleted.');
      loadExhibitors();
    }
  };

  return (
    <PageLayout activePage="exhibitors">
      <div className="exhibitors-admin-page">
        <div className="exhibitors-admin-header">
          <div>
            <h1>Exhibitors Management</h1>
            <p>Manage publishers and booths</p>
          </div>
        </div>

        {message && <div className="exhibitors-message">{message}</div>}

        <div className="exhibitors-stats-grid">
          <div className="exhibitor-stat-card">
            <span>Total Exhibitors</span>
            <h2>{stats.total}</h2>
            <p>+24 new</p>
          </div>

          <div className="exhibitor-stat-card">
            <span>Booths Reserved</span>
            <h2>{stats.approved}</h2>
            <p>96% occupancy</p>
          </div>

          <div className="exhibitor-stat-card">
            <span>International</span>
            <h2>{stats.international}</h2>
            <p>+12 countries</p>
          </div>

          <div className="exhibitor-stat-card">
            <span>Pending</span>
            <h2>{stats.pending}</h2>
            <p>Need review</p>
          </div>
        </div>

        <div className="exhibitors-toolbar">
          <input
            type="text"
            placeholder="Search exhibitors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="Approved">Approved</option>
            <option value="Pending">Pending</option>
          </select>

          <button onClick={resetForm}>+ Add Exhibitor</button>
        </div>

        {loading ? (
          <div className="exhibitors-loading">Loading exhibitors...</div>
        ) : (
          <div className="exhibitors-admin-grid">
            {filteredExhibitors.map((exhibitor) => (
              <div className="exhibitor-admin-card" key={exhibitor.id}>
                <div className="exhibitor-card-top">
                <div className="exhibitor-logo-box">
  {exhibitor.logo_url ? (
    <img src={exhibitor.logo_url} alt={exhibitor.name} />
  ) : (
    exhibitor.logo_emoji
  )}
</div>
                  <div>
                    <h3>{exhibitor.name}</h3>
                    <p>{exhibitor.category}</p>
                    <span className={`status-badge ${exhibitor.status?.toLowerCase()}`}>
                      {exhibitor.status}
                    </span>
                  </div>
                  {exhibitor.is_featured && <span className="featured-star">★</span>}
                </div>

                <div className="exhibitor-meta">
                  <p>⌖ Booth {exhibitor.booth} - {exhibitor.hall}</p>
                  <p>◎ {exhibitor.website}</p>
                </div>

                <div className="exhibitor-actions">
                  <button onClick={() => editExhibitor(exhibitor)}>✎ Edit</button>
                  <button className="delete-btn" onClick={() => deleteExhibitor(exhibitor.id)}>
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="add-exhibitor-panel">
          <h2>{editingId ? 'Edit Exhibitor' : 'Add New Exhibitor'}</h2>

          <div className="exhibitor-form-grid">
            <label>
              Exhibitor Name
              <input value={form.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Full name" />
            </label>

            <label>
              Category
              <input value={form.category} onChange={(e) => handleChange('category', e.target.value)} placeholder="Arabic Books" />
            </label>

            <label>
              Description
              <input value={form.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Short description" />
            </label>

            <label>
              Audience
              <select value={form.audience} onChange={(e) => handleChange('audience', e.target.value)}>
                <option value="">Select audience</option>
                <option value="Kids">Kids</option>
                <option value="Young Adults">Young Adults</option>
                <option value="Adults">Adults</option>
              </select>
            </label>

            <label>
              Language
              <select value={form.language} onChange={(e) => handleChange('language', e.target.value)}>
                <option value="">Select language</option>
                <option value="Arabic">Arabic</option>
                <option value="English">English</option>
                <option value="French">French</option>
                <option value="Other">Other</option>
              </select>
            </label>

            <label>
              Price Range
              <select value={form.price_range} onChange={(e) => handleChange('price_range', e.target.value)}>
                <option value="">Select price</option>
                <option value="Budget">Budget</option>
                <option value="Mid-range">Mid-range</option>
                <option value="Premium">Premium</option>
              </select>
            </label>

            <label>
              Booth Number
              <input value={form.booth} onChange={(e) => handleChange('booth', e.target.value)} placeholder="A-142" />
            </label>

            <label>
              Hall
              <input value={form.hall} onChange={(e) => handleChange('hall', e.target.value)} placeholder="Hall 1" />
            </label>

            <label>
              Email
              <input value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="contact@example.com" />
            </label>

            <label>
              Website
              <input value={form.website} onChange={(e) => handleChange('website', e.target.value)} placeholder="www.example.com" />
            </label>

            <label>
  Logo URL
  <input
    value={form.logo_url}
    onChange={(e) => handleChange('logo_url', e.target.value)}
    placeholder="https://example.com/logo.png"
  />
</label>

            <label>
              Status
              <select value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
              </select>
            </label>
          </div>

          <div className="exhibitor-checks">
            <label>
              <input
                type="checkbox"
                checked={form.is_international}
                onChange={(e) => handleChange('is_international', e.target.checked)}
              />
              International exhibitor
            </label>

            <label>
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => handleChange('is_featured', e.target.checked)}
              />
              Featured exhibitor
            </label>
          </div>

          <div className="form-actions">
            <button onClick={saveExhibitor}>
              {editingId ? 'Save Changes' : 'Add Exhibitor'}
            </button>
            <button className="cancel-btn" onClick={resetForm}>Cancel</button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}